import React from "react";
import { DanyAvatar } from "./DanyAvatar";
import { DanyProse } from "./DanyProse";
import { Avatar } from "../core/Avatar";
import { Icon } from "../core/Icon";

/**
 * One turn in a Dany thread. The client's turn is a linen container; Dany's turn
 * is unboxed body copy on the page — the surface should read as a letter, not a
 * chat app. Only Dany's turns carry inline widgets (children).
 *
 * PORT NOTE (DY1.4): three additions.
 *
 * `avatarState` — the shipped component hard-coded `speaking`, whose pupils are
 * a two-channel level meter that loops forever, so in a real transcript every
 * past turn kept "talking" indefinitely. The live turn passes the streaming
 * state (settled to `idle` within 3s of the turn ending, see
 * `useDanyAvatarState`). Default stays `speaking` so a single-turn specimen
 * still looks like the design system's card.
 *
 * `showAvatar` — in a thread, only the LAST Dany turn carries a face. A column
 * of identical bees down the side of a conversation is not identity, it is
 * wallpaper, and it fights the rule this surface is built on: Dany is unboxed
 * and reads as a letter, and a letter does not reprint the sender's portrait
 * beside every paragraph. It also guarantees what Jorge asked for — never two
 * animated Danys at once — structurally rather than by coordinating states.
 *
 * The text now renders through `DanyProse` instead of a bare `<p>`, so a stray
 * `**bold**` from the model is emphasis rather than literal asterisks.
 *
 * `steps` — `DanyProse` renders a run of numbered lines as steps (prompt
 * v1.2.x). Pass `false` for a turn where numbered lines cannot be steps, so the
 * caller's knowledge of what the turn contains beats the model's punctuation.
 */
export function DanyTurn({
  from = "dany", text, children, thinking = false, meta, danyArt,
  userName = "You", avatarState, showAvatar = true, steps = true,
}) {
  const isDany = from === "dany";
  if (!isDany) {
    return (
      <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start", justifyContent: "flex-end" }}>
        <div style={{
          // The bubble is a flex item: without a minimum of 0 it refuses to
          // shrink below its longest word, and on a phone that pushes the whole
          // row — avatar included — past the right padding.
          maxWidth: "min(84%, 52ch)", minWidth: 0,
          overflowWrap: "anywhere", padding: "12px 16px",
          background: "var(--dany-bubble-user)", border: "1px solid var(--dany-bubble-user-border)",
          borderRadius: "var(--radius-md) var(--radius-md) 6px var(--radius-md)",
          fontFamily: "var(--font-body)", fontSize: "var(--text-base)",
          lineHeight: "var(--leading-relaxed)", color: "var(--dany-text)",
        }}>{text}</div>
        <Avatar name={userName} size="sm" />
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start" }}>
      {/* The gutter is held whether or not a face is in it, so every Dany turn
          in a thread starts on the same left edge. Dropping the width with the
          avatar would make the reading column jump on the last turn. */}
      <span style={{ marginTop: 2, width: 28, flex: "0 0 auto" }}>
        {showAvatar && (
          <DanyAvatar size={28} state={avatarState || (thinking ? "thinking" : "speaking")} src={danyArt} />
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
        {thinking ? (
          <p style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            fontFamily: "var(--font-body)", fontSize: "var(--text-base)", color: "var(--dany-meta)",
          }}>
            <Icon name="loader-2" size={15} style={{ animation: "fixy-spin 1s linear infinite" }} />
            {text || "Looking…"}
          </p>
        ) : (
          <DanyProse text={text} steps={steps} />
        )}
        {children && <div style={{ marginTop: "var(--dany-gap-block)" }}>{children}</div>}
        {meta && (
          <p style={{ marginTop: "var(--space-2)", fontSize: "var(--text-xs)", color: "var(--dany-meta)" }}>{meta}</p>
        )}
      </div>
    </div>
  );
}
